import { ItemCategoryGroup, Status } from '@/assets/enums'

// Toutes les commandes fournisseur brouillon déjà rattachées à un article ou
// à l'un de ses composants, dédupliquées — utilisé pour proposer un
// rattachement à une commande existante de la famille plutôt que d'en
// recréer une par composant. Seules les commandes en brouillon (DRA) sont
// proposées : une commande déjà envoyée au fournisseur ne doit plus recevoir
// de nouvelles lignes depuis cet écran.
export function getFamilySupplierOrders(itemDossier) {
  const ownOrders = itemDossier?.supplier_orders ?? []
  const componentOrders = (itemDossier?.components ?? []).flatMap(
    (component) => component.supplier_orders ?? []
  )
  const seen = new Set()
  return [...ownOrders, ...componentOrders].filter((order) => {
    if (order.status !== Status.DRAFT) return false
    if (seen.has(order.id)) return false
    seen.add(order.id)
    return true
  })
}

// Un article (principal ou composant) peut être rattaché à une commande
// fournisseur dès que le back a déterminé que cet article dossier précis
// est éligible (`can_add_to_supplier_order`, ItemDossierResource::
// getCanAddToSupplierOrder(), basé sur orderableQuantity() — jamais
// restreint par order_mode ni par stock_management_enabled, seul NO_ORDER
// est exclu). Ce bouton reste nécessaire même gestion de stock désactivée :
// un article en commande manuelle y reste toujours non rattaché par design,
// et doit pouvoir l'être à la main (BBSW-10835).
export function canAddItemDossierToSupplierOrder(itemDossier) {
  return Boolean(itemDossier?.can_add_to_supplier_order)
}

// Un article dont la catégorie appartient au groupe "fleurs" — détermine si
// les valeurs par défaut du dossier (fournisseur/lieu/date) s'appliquent.
// Le groupe est porté par la catégorie parente : une sous-catégorie (ex.
// "Kunstbloemen" sous "Fleur") hérite de celui de son parent.
export function isItemDossierInFlowersCategory(
  itemDossier,
  itemCategoriesGroupByParent
) {
  const categoryId = itemDossier?.item_category_id
  const category = itemCategoriesGroupByParent?.find(
    (c) =>
      c.id === categoryId ||
      c.children?.some((child) => child.id === categoryId)
  )
  return category?.group_id === ItemCategoryGroup.FLOWERS
}

// Le back ne renseigne `has_item_dossiers` que pour les articles : on la
// complète avec les item_dossiers/invoice_items des prestations, toujours
// présents sur `supplier_order_items` même sans `item_stocks`.
export function supplierOrderHasItemDossiers(supplierOrder) {
  return !!(
    supplierOrder?.has_item_dossiers ||
    supplierOrder?.supplier_order_items?.some(
      (item) =>
        item.item_stocks?.some((stock) => stock.item_dossier_id) ||
        item.item_dossiers?.length > 0 ||
        item.invoice_items?.length > 0
    )
  )
}

// Un élément de `originalRequest` a besoin de sa propre page de sélection stock si :
// - il a un `item` à traiter (une entrée sans `item` n'a rien à afficher elle-même), ET
// - il n'est pas imbriqué dans la page de son parent : un composant n'est imbriqué que si
//   son article parent a lui-même un `item` (donc sa propre page, avec la section
//   "composants liés" qui l'affichera). Si le parent n'a pas de `item` (ex. NO_ORDER, rien
//   à sélectionner pour lui-même), ses composants s'affichent chacun en page autonome.
export function getStockablePageIndexes(originalRequest) {
  if (!Array.isArray(originalRequest)) return []
  return originalRequest
    .map((request, idx) => {
      if (!request?.item) return null

      if (request.component_parent_id) {
        const parent = originalRequest.find(
          (entry) => entry?.id === request.component_parent_id
        )
        if (parent?.item) {
          // Le parent a sa propre page : ce composant y est imbriqué.
          return null
        }
      }

      return idx
    })
    .filter((idx) => idx !== null)
}
